<template>
  <div>
    <b-overlay
      variant="white"
      :show="isLoading"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
    >
      <b-card>
        <b-card-body id="customers-card-body">
          <b-modal
            id="add-customer-document-modal"
            ref="add-customer-document-modal"
            cancel-variant="outline-secondary"
            :ok-title="t('Save')"
            :cancel-title="t('Close')"
            centered
            :title="t('Add New Document')"
            size="sm"
            :ok-disabled="submitting"
            @hidden="resetModal"
            @ok="handleOk"
          >
            <validation-observer ref="customerNewDocumentForm">
              <b-form>
                <b-row>
                  <b-col sm="12">
                    <b-form-group>
                      <div class="d-flex form-group-label">
                        <label for="name">{{ t('Document Type') }}</label>
                        <span class="text-danger">*</span>
                      </div>
                      <validation-provider
                        #default="{ errors }"
                        :name="t('Document Type')"
                        rules="required"
                      >
                        <b-form-select
                          id="type"
                          v-model="type"
                          name="type"
                          :class="[errors[0] && 'is-invalid']"
                          :options="documentTypesOption"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>

                    <b-form-group>
                      <div class="d-flex form-group-label">
                        <label for="name">{{ t('Document') }}</label>
                        <span class="text-danger">*</span>
                      </div>
                      <validation-provider
                        #default="{ errors }"
                        :name="t('Document')"
                        rules="required"
                      >
                        <b-form-file
                          v-model="files"
                          :placeholder="t('Choose a file or drop it here')"
                          :drop-placeholder="t('Drop file here')"
                          multiple
                          :file-name-formatter="formatNames"
                          :browse-text="t('Select File')"
                          accept=".jpg, .jpeg, .png, .pdf"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-form>
            </validation-observer>
          </b-modal>

          <div
            v-if="showWarningMessage"
            class="border border-warning bg-warning-subtle rounded p-1 mb-1"
          >
            {{ t('Profile Document Upload Warning') }}
          </div>

          <vue-good-table
            v-if="shouldRenderTable"
            mode="remote"
            :sort-options="{ enabled: false }"
            :total-rows="totalRecords"
            :is-loading.sync="isLoading"
            :show-footer="true"
            :rows="rows"
            :columns="getColumns"
            :pagination-options="{
              enabled: true,
              jumpFirstOrLast: true,
              rowsPerPageLabel: t('Rows per page'),
              ofLabel: t('ofDocument'),
              nextLabel: t('nextLabel'),
              prevLabel: t('prevLabel'),
              dropdownAllowAll: false
            }"
            @on-page-change="onPageChange"
            @on-per-page-change="onPerPageChange"
          >
            <template slot="table-row" slot-scope="props">
              <span v-if="props.column.field === 'documentTypeText'">
                {{ props.row.documentTypeText || '-' }}
              </span>
              <span v-else-if="props.column.field === 'statusText'">
                {{ props.row.statusText || '-' }}
              </span>
              <span v-else-if="props.column.field === 'fileName'">
                {{ props.row.fileName || '-' }}
              </span>
              <span v-else-if="props.column.field === 'fileSize'">
                {{ props.row.fileSize || '-' }}
              </span>
              <span v-else-if="props.column.field === 'mimeType'">
                {{ props.row.mimeType || '-' }}
              </span>
              <span v-if="props.column.field === 'actions'">
                <span class="d-flex justify-content-center table-icon-buttons">
                  <b-button
                    :id="`download-document-button-${props.row.id}`"
                    variant="primary"
                    type="button"
                    size="sm"
                    @click="downloadCustomerDocument(props.row)"
                  >
                    <feather-icon icon="DownloadIcon" size="15" />
                    <b-tooltip
                      triggers="hover"
                      :target="`download-document-button-${props.row.id}`"
                      :title="t('Download Document')"
                      :delay="{ show: 0, hide: 0 }"
                      boundary="viewport"
                    />
                  </b-button>
                  <b-button
                    v-if="shouldRenderDeleteButton(props.row.status)"
                    :id="`delete-document-button-${props.row.id}`"
                    variant="danger"
                    type="button"
                    size="sm"
                    @click="deleteCustomerDocument(props.row.id)"
                  >
                    <feather-icon icon="XIcon" size="15" />
                    <b-tooltip
                      triggers="hover"
                      :target="`delete-document-button-${props.row.id}`"
                      :title="t('Delete Document')"
                      :delay="{ show: 0, hide: 0 }"
                      boundary="viewport"
                    />
                  </b-button>
                </span>
              </span>
            </template>
          </vue-good-table>
        </b-card-body>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BFormGroup,
  BFormSelect,
  BRow,
  BCol,
  BCard,
  BCardBody,
  BOverlay,
  BForm,
  BFormFile,
  BTooltip,
  BButton
} from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { VueGoodTable } from 'vue-good-table';
import { helper } from '@core/mixins/helper.js';
import { mapGetters } from 'vuex';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import useService from '@/auth/service/useService';
import { downloadBlobFile } from '@/@core/utils/utils';

export default {
  components: {
    VueGoodTable,
    BFormGroup,
    BFormSelect,
    BRow,
    BCol,
    BCard,
    BCardBody,
    BOverlay,
    BForm,
    BFormFile,
    BTooltip,
    BButton,

    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,

    ValidationProvider,
    ValidationObserver
  },

  mixins: [helper],

  data() {
    return {
      isLoading: false,
      files: null,
      submitting: false,
      type: '',
      rows: [],
      totalRecords: 0,
      filter: {
        page: 1,
        limit: 10
      },
      documentTypes: []
    };
  },

  computed: {
    ...mapGetters({
      authUser: 'auth/authUser'
    }),
    getColumns() {
      if (!this.rows.length) {
        return [];
      }

      return [
        {
          label: this.$t('Document Type'),
          field: 'documentTypeText'
        },
        {
          label: this.$t('Status'),
          field: 'statusText'
        },
        {
          label: this.$t('File Name'),
          field: 'fileName'
        },
        {
          label: this.$t('File Size'),
          field: 'fileSize'
        },
        {
          label: this.$t('Mime Type'),
          field: 'mimeType'
        },
        {
          label: this.$t('Actions'),
          field: 'actions'
        }
      ];
    },

    shouldRenderTable() {
      return this.getColumns.length > 0;
    },

    documentTypesOption() {
      const options = [{ text: this.$t('Make a Choice'), value: '' }];
      if (this.documentTypes && this.documentTypes.length > 0) {
        options.push(
          ...this.documentTypes.map(item => ({
            text: item.info,
            value: item.value
          }))
        );
      }
      return options;
    },
    showWarningMessage() {
      if (!this.rows.length && this.isLoading) return false;

      return (
        this.authUser.customer &&
        !this.authUser.customer.status &&
        !this.documentTypes.every(document =>
          this.rows.some(row => row.documentType === document.value)
        )
      );
    }
  },

  async created() {
    this.loadItems();
    this.documentTypes = await this.getCustomerDocumentType();
  },

  methods: {
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },

    async loadItems() {
      let requestData = this.filter;

      requestData = {
        ...this.filter
      };

      this.isLoading = true;
      await useService
        .getAllCustomerDocuments(requestData)
        .then(async response => {
          const obj = response.data;
          this.totalRecords = response.meta.total;
          const statusResponse = await useService.getCustomerDocumentStatus();
          const documentTypeResponse = await useService.getCustomerDocumentType();

          this.rows = obj.map(row => {
            const foundObject = documentTypeResponse.data.find(j => j.value === row.documentType);
            const statusInfo = statusResponse.data.find(status => status.value === row.status);
            let documentTypeText = '-';
            let statusText = '-';

            if (foundObject) {
              documentTypeText = foundObject.info || '-';
            }

            if (statusInfo) {
              statusText = statusInfo.info;
            }

            return {
              ...row,
              documentTypeText,
              statusText
            };
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    deleteCustomerDocument(id) {
      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Delete Document Warning')}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      }).then(async result => {
        if (result.value) {
          await this.deleteCustomerDocumentRequest(id)
            .then(response => {
              this.showSuccessMessage(
                `${response.message || this.$t('Deleted Documents Successfully')}`
              );

              this.filter.page = 1;

              this.loadItems();
            })
            .catch(messages => {
              if (typeof messages === 'object' && messages && messages.length) {
                messages.forEach(item => {
                  this.showErrorMessage(item.message);
                });
              }
            });
        }
      });
    },

    async downloadCustomerDocument(document) {
      const res = await useService.downloadCustomerDocument(document.id);
      downloadBlobFile({ file: res, fileName: document.fileName });
    },

    async deleteCustomerDocumentRequest(id) {
      return useService.deleteCustomerDocument(id);
    },

    resetModal() {
      this.files = null;
      this.type = '';
    },

    handleSubmit() {
      this.$refs.customerNewDocumentForm.validate().then(success => {
        if (success) {
          const formData = new FormData();
          if (this.files) {
            this.files.forEach(file => {
              formData.append('document_file', file);
              formData.append('document_type', this.type);
            });
          }

          this.submitting = true;
          useService
            .uploadCustomerDocuments(formData)
            .then(response => {
              this.$refs['add-customer-document-modal'].hide();
              this.showSuccessMessage(
                `${response.message || this.$t('Upload Document Successfully')}`
              );
              this.loadItems();
            })
            .catch(messages => {
              if (typeof messages === 'object' && messages && messages.length) {
                messages.forEach(item => {
                  this.showErrorMessage(item.message);
                });
              }
            })
            .finally(() => {
              this.submitting = false;
            });
        }
      });
    },

    formatNames(files) {
      return files.length === 1 ? files[0].name : `${files.length} files selected`;
    },

    handleOk(bvModalEvent) {
      if (this.submitting) return false;

      bvModalEvent.preventDefault();
      this.handleSubmit();

      return true;
    },

    shouldRenderDeleteButton(status) {
      return status !== '1' && status !== 'Döküman onaylandı.';
    }
  },

  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
